import { useEffect, useState } from 'react';

import {
  ActionIcon,
  Alert,
  Button,
  Checkbox,
  Group,
  Select,
  Stack,
  Text,
  Tooltip,
} from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';

import { Composer, type ComposerSendRequest } from '@/components/Chat/Composer';
import {
  EMPTY_QUOTE,
  QuoteFields,
  quoteRequestFrom,
  type QuoteDraft,
} from '@/components/Modals/RegistryOrderQuoteFields';
import {
  canRecordRegistryQuote,
  REGISTRY_ORDER_REPLY_TEMPLATES,
  REGISTRY_ORDER_REPLY_TEXT_MAX,
  type RegistryOrderReplyRequest,
} from '@/types/registry-documents';
import { formatRegistryMessageTime } from '../RegistryOrdersCard/RegistryOrderMessages';
import type { RegistryReplyTarget } from './conversationTargets';
import {
  describeReplyFailure,
  NOTHING_TO_ANSWER,
  QUOTE_INCOMPLETE,
  REPLY_NOT_CONFIGURED_MESSAGE,
  type ReplyFailure,
} from './replyWords';
import classes from './RegistryConversation.module.css';

/** "GTI-5F6A7B8C9D · 01 Oct 2026, 14:41", plus "answered" once it is. */
function targetLabel(target: RegistryReplyTarget): string {
  const when = formatRegistryMessageTime(target.message.receivedAt);
  const parts = [target.order.reference, when, target.message.answered ? 'answered' : null];
  return parts.filter(Boolean).join(' · ');
}

interface RegistryReplyComposerProps {
  /** Replies can be sent from the Hub in this environment. */
  replyEnabled: boolean;
  /** Every email a reply may answer, oldest first. */
  targets: RegistryReplyTarget[];
  /** The email the reply answers; null when there is none. */
  target: RegistryReplyTarget | null;
  onTargetChange: (targetKey: string) => void;
  /** Send the reply, then re-read. Throws on failure: the composer keeps
   * the text and says why. */
  onReply: (orderId: string, request: RegistryOrderReplyRequest) => Promise<unknown>;
  /** A reply went: the composer's choice of email is spent. */
  onSent: () => void;
}

/**
 * The thread's composer, inline at the bottom like the Client chat's. A
 * reply always names the desk's email it answers (the mailbox threads it
 * there), so the header says which, with a way to change it where more
 * than one is open; the two answers a quote usually gets are one click
 * away, and an order with no expected date yet can record the desk's
 * quote with the reply. The composer is kept short so the thread above
 * it has room on an ordinary window: how the reply travels (the shared
 * mailbox, the desk's message quoted, the signature) sits behind an info
 * icon on the header, the templates are labelled by their gist, and the
 * quote's note is one line.
 *
 * An environment without a send token refuses every reply (503); the
 * composer then says so and offers no second attempt.
 */
export function RegistryReplyComposer({
  replyEnabled,
  targets,
  target,
  onTargetChange,
  onReply,
  onSent,
}: Readonly<RegistryReplyComposerProps>) {
  // The nonce lets the same template seed the box twice: once edited, the
  // text differs from the template, and a second click must restore it.
  const [seed, setSeed] = useState({ text: '', nonce: 0 });
  const [recordQuote, setRecordQuote] = useState(false);
  const [quote, setQuote] = useState<QuoteDraft>(EMPTY_QUOTE);
  const [changing, setChanging] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [failure, setFailure] = useState<ReplyFailure | null>(null);

  // A quote belongs to the order it was typed for.
  const orderId = target?.order.id ?? null;
  useEffect(() => {
    setRecordQuote(false);
    setQuote(EMPTY_QUOTE);
  }, [orderId]);

  // The same rule as recording the quote on its own, so the two never
  // disagree: an order not yet confirmed at Armadillo (`pending`) is not
  // offered the quote with the reply either.
  const quoteOffered = target !== null && canRecordRegistryQuote(target.order);
  let blocked: string | null = null;
  if (!replyEnabled) {
    blocked = REPLY_NOT_CONFIGURED_MESSAGE;
  } else if (!target) {
    blocked = NOTHING_TO_ANSWER;
  }
  const unsendable = blocked !== null || failure?.final === true;

  const send = async ({ bodyText }: ComposerSendRequest) => {
    if (!target?.message.messageId) {
      return;
    }
    const quoteRequest = quoteOffered && recordQuote ? quoteRequestFrom(quote) : null;
    if (quoteOffered && recordQuote && quoteRequest === null) {
      setFailure(QUOTE_INCOMPLETE);
      // Rejected so the composer keeps the text.
      throw new Error(QUOTE_INCOMPLETE.message);
    }
    setSubmitting(true);
    setFailure(null);
    try {
      await onReply(target.order.id, {
        in_reply_to: target.message.messageId,
        text: bodyText.trim(),
        quote: quoteRequest,
      });
      setRecordQuote(false);
      setQuote(EMPTY_QUOTE);
      onSent();
    } catch (err) {
      setFailure(describeReplyFailure(err));
      throw err;
    } finally {
      setSubmitting(false);
    }
  };

  const header = blocked ? (
    <Text size="xs" c="dimmed" data-testid="registry-conversation-blocked">
      {blocked}
    </Text>
  ) : (
    target && (
      <Stack gap="xs">
        <Group gap={6} wrap="wrap" align="center" data-testid="registry-conversation-answering">
          <Text size="xs" c="dimmed">
            Answering
          </Text>
          <Text size="xs" fw={600} ff="monospace">
            {target.order.reference}
          </Text>
          <Text size="xs" c="dimmed">
            {[formatRegistryMessageTime(target.message.receivedAt), 'Armadillo']
              .filter(Boolean)
              .map((part) => `· ${part}`)
              .join(' ')}
          </Text>
          <Tooltip label={howTheReplyTravels(target)} multiline w={300} withArrow>
            <ActionIcon
              variant="subtle"
              color="gray"
              size="xs"
              aria-label="How the reply is sent"
              data-testid="registry-conversation-how"
            >
              <IconInfoCircle size={14} />
            </ActionIcon>
          </Tooltip>
          {targets.length > 1 && !changing && (
            <Button
              size="compact-xs"
              variant="subtle"
              onClick={() => setChanging(true)}
              disabled={submitting}
              data-testid="registry-conversation-change"
            >
              Change
            </Button>
          )}
          {targets.length > 1 && changing && (
            <Select
              size="xs"
              aria-label="Answering"
              data={targets.map((t) => ({ value: t.key, label: targetLabel(t) }))}
              value={target.key}
              onChange={(key) => {
                if (key) {
                  onTargetChange(key);
                }
                setChanging(false);
              }}
              allowDeselect={false}
              style={{ flex: '1 1 12rem' }}
              data-testid="registry-conversation-target"
            />
          )}
        </Group>
        <Group gap="xs">
          {REGISTRY_ORDER_REPLY_TEMPLATES.map((template) => (
            <Button
              key={template.text}
              size="compact-xs"
              variant="light"
              title={template.text}
              onClick={() => setSeed((s) => ({ text: template.text, nonce: s.nonce + 1 }))}
              disabled={submitting || unsendable}
            >
              {template.label}
            </Button>
          ))}
        </Group>
        {quoteOffered && (
          <Stack gap="xs" data-testid="registry-conversation-quote">
            <Checkbox
              size="xs"
              label="Record a quote with this reply"
              description="Sets the expected date, so a late order is chased"
              checked={recordQuote}
              onChange={(e) => setRecordQuote(e.currentTarget.checked)}
              disabled={submitting || unsendable}
            />
            {recordQuote && <QuoteFields draft={quote} onChange={setQuote} />}
          </Stack>
        )}
      </Stack>
    )
  );

  return (
    <Stack gap="xs" className={classes.composer}>
      {failure && (
        <Alert
          color={failure.final ? 'orange' : 'red'}
          title={failure.title}
          data-testid="registry-conversation-failure"
        >
          {failure.message}
        </Alert>
      )}
      <Composer
        canSendPrivate={false}
        onSend={send}
        isSending={submitting}
        disabled={unsendable}
        maxLength={REGISTRY_ORDER_REPLY_TEXT_MAX}
        seedText={seed.text}
        seedKey={seed.nonce}
        header={header}
      />
    </Stack>
  );
}

/** What the info icon on the composer's header says about the reply. */
function howTheReplyTravels(target: RegistryReplyTarget): string {
  const to = target.message.sender?.trim() || 'the research desk';
  return (
    `Sent from the shared mailbox to ${to} as a reply to this message, ` +
    'with their message quoted under yours and "GTI Hub" as the signature.'
  );
}
